import React, { useState, useEffect, useRef } from 'react';
import type { WidgetItem, ReportPage, WidgetType, ReportGlobalSettings } from './types';
import GridLayout, { verticalCompactor } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Box, Typography, Button } from '@mui/material';
import Chart from 'react-apexcharts';
import { Plus, MoreHorizontal, Copy, Trash2, UploadCloud, Upload, Loader2, AlignLeft, AlignCenter, AlignRight, ChevronDown, Maximize2, ArrowUp, ArrowDown } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';

interface ReportCanvasProps {
  pages: ReportPage[];
  activePageId: string;
  selectedId: string | null;
  globalSettings?: ReportGlobalSettings;
  readOnly?: boolean;
  activeReportId?: string;
  onSelectWidget: (id: string | null, pageId?: string) => void;
  onLayoutChange: (pageId: string, layout: Layout) => void;
  onAddPage: () => void;
  onDuplicateWidget?: (id: string) => void;
  onDeleteWidget?: (id: string) => void;
  onEditWidget?: (widget: WidgetItem) => void;
  onDropWidget?: (type: WidgetType, pageId: string, gridPos?: { x: number; y: number }) => void;
  onUpdateWidget?: (id: string, updates: Partial<WidgetItem['properties']>) => void;
  onMoveWidgetToPage?: (widgetId: string, direction: 'prev' | 'next') => void;
}

const COLOR_PALETTES_MAP: Record<string, string[]> = {
  classic: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
  indigo: ['#6366f1', '#38bdf8', '#ffffffff', '#ec4899', '#14b8a6', '#f59e0b'],
  emerald: ['#10b981', '#06b6d4', '#84cc16', '#3b82f6', '#14b8a6', '#eab308'],
  amber: ['#f59e0b', '#f97316', '#ef4444', '#eab308', '#84cc16', '#06b6d4'],
  rose: ['#f43f5e', '#ec4899', '#8b5cf6', '#3b82f6', '#06b6d4', '#10b981'],
  ocean: ['#0284c7', '#06b6d4', '#0d9488', '#3b82f6', '#6366f1', '#8b5cf6'],
  sunset: ['#f97316', '#fbbf24', '#f43f5e', '#8b5cf6', '#06b6d4', '#10b981'],
  cyber: ['#06b6d4', '#a855f7', '#ec4899', '#3b82f6', '#10b981', '#f59e0b']
};

function ReportChartRenderer({
  widget,
  accent,
  colors,
  properties,
  readOnly = false,
}: {
  widget: WidgetItem;
  accent: string;
  colors: string[];
  properties: any;
  readOnly?: boolean;
}) {
  const [queryResult, setQueryResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const safeParse = (val: any) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return null;
      }
    }
    return null;
  };

  let queryDef = safeParse(properties?.query_definition);
  if (!queryDef && (properties?.dataSource || properties?.dataset_name)) {
    const src = properties.dataSource || properties.dataset_name;
    const metricType = properties.aggregation || properties.yAxisAggregation || 'count';
    const metricField = properties.metric_field || properties.yAxisField || null;
    const xAxisField = properties.xAxisField || properties.group_by_field || null;
    queryDef = {
      source: src,
      metrics: [{ type: metricType, field: metricField, alias: metricField || 'metric' }],
      x_axis: xAxisField ? { field: xAxisField } : null,
      filters: properties.filters || [],
      columns: properties.columns || [],
      limit: 100
    };
  }

  const visConfig = safeParse(properties?.visualization_config) || {};

  useEffect(() => {
    if (properties.preloadedRows || !queryDef?.source) return;
    let isMounted = true;
    const fetchQuery = async () => {
      setLoading(true);
      try {
        const storedLevel = localStorage.getItem('level');
        const levelObj = storedLevel ? JSON.parse(storedLevel) : null;
        const params: any = {};
        const qDef = JSON.parse(JSON.stringify(queryDef));
        if (levelObj && levelObj.name !== 'global' && levelObj.id) {
          params.client_id = levelObj.id;
          qDef.client_id = levelObj.id;
        }
        const res = await baseapi.post('/cdash/query', qDef, { params });
        if (isMounted) {
          setQueryResult(res.data);
        }
      } catch (err) {
        console.error('Failed to query cdash data for chart:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchQuery();
    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(queryDef), properties.preloadedRows]);

  if (queryDef && loading && !queryResult && !properties.preloadedRows) {
    if (readOnly) {
      return <Box sx={{ flex: 1, minHeight: 0 }} />;
    }
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Loading chart data...</Typography>
      </Box>
    );
  }

  const chartType = properties.chartType || properties.chart_type || properties.widget_type || widget.type || 'bar';

  // Extract live rows and columns from API response or preloaded chunks
  const rawRows: any[] = Array.isArray(properties.preloadedRows)
    ? properties.preloadedRows
    : (Array.isArray(queryResult?.rows)
      ? queryResult.rows
      : (Array.isArray(queryResult?.data?.rows)
        ? queryResult.data.rows
        : []));
  const rawColumns: string[] = Array.isArray(properties.preloadedColumns)
    ? properties.preloadedColumns
    : (Array.isArray(queryResult?.columns)
      ? queryResult.columns
      : (Array.isArray(queryResult?.data?.columns)
        ? queryResult.data.columns
        : []));

  // Metric / KPI Card
  if (chartType === 'metric') {
    const totalVal = queryResult?.unfiltered_total !== undefined
      ? queryResult.unfiltered_total
      : (rawRows.length > 0 ? (rawRows[0][1] ?? rawRows[0][0] ?? 0) : 1428);
    const prefix = properties.metricPrefix || '';
    const suffix = properties.metricSuffix || '';
    const paletteColors = properties.colorPalette && COLOR_PALETTES_MAP[properties.colorPalette];
    const metricAccent = properties.accentColor || (paletteColors ? paletteColors[0] : (accent || '#3b82f6'));

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1 }}>
        <Typography sx={{ fontSize: 34, fontWeight: 900, color: metricAccent, lineHeight: 1 }}>
          {prefix}{typeof totalVal === 'number' ? totalVal.toLocaleString() : totalVal}{suffix}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.75, fontWeight: 600 }}>
          {visConfig?.label || properties.yAxisLabel || properties.yAxisAggregation?.toUpperCase() || 'TOTAL COUNT'}
        </Typography>
      </Box>
    );
  }

  // Table View
  if (chartType === 'table') {
    const tableCols = rawColumns.length > 0
      ? rawColumns
      : [properties.xAxisField || 'Category', properties.yAxisLabel || 'Count'];

    const limit = properties.query_definition?.limit !== undefined
      ? properties.query_definition.limit
      : (properties.limit !== undefined ? properties.limit : 10);

    const displayRows = (rawRows.length > 0 && limit && limit < 1000)
      ? rawRows.slice(0, limit)
      : rawRows;

    const colCount = tableCols.length;
    const density = properties.density || 'auto';
    const wrapHeaders = properties.wrapHeaders !== false;
    const fitToPage = properties.fitToPage !== false;

    const isUltraDense = colCount > 10;
    const isDense = colCount > 7 || density === 'compact';
    const isMedium = colCount > 4 || density === 'comfortable' ? false : true;

    const headerFontSize = isUltraDense ? 7.5 : (isDense ? 8 : (isMedium ? 9 : 10));
    const bodyFontSize = isUltraDense ? 7 : (isDense ? 7.5 : (isMedium ? 8.5 : 9.5));
    const cellPadding = isUltraDense ? '2px 3px' : (isDense ? '3px 4px' : (isMedium ? '4px 6px' : '6px 8px'));

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontSize: bodyFontSize, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#ffffff', width: '100%', maxWidth: '100%' }}>
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            overflowX: 'hidden',
            overflowY: 'hidden',
            minHeight: 0,
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <table
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: '100%',
              borderCollapse: 'collapse',
              textAlign: 'left',
              tableLayout: 'fixed'
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 2 }}>
                {tableCols.map((col, cIdx) => (
                  <th
                    key={cIdx}
                    style={{
                      padding: cellPadding,
                      fontSize: headerFontSize,
                      fontWeight: 700,
                      color: '#475569',
                      textTransform: 'capitalize',
                      whiteSpace: wrapHeaders ? 'normal' : 'nowrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      lineHeight: 1.15,
                      borderRight: cIdx < tableCols.length - 1 ? '1px solid #f1f5f9' : 'none',
                      width: fitToPage ? `${(100 / colCount).toFixed(2)}%` : 'auto'
                    }}
                  >
                    {col.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.length > 0 ? (
                displayRows.map((row, rIdx) => (
                  <tr key={rIdx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: rIdx % 2 === 1 ? '#fafafa' : '#ffffff' }}>
                    {tableCols.map((_, cIdx) => (
                      <td
                        key={cIdx}
                        style={{
                          padding: cellPadding,
                          fontSize: bodyFontSize,
                          color: cIdx === 0 ? '#334155' : accent,
                          fontWeight: cIdx === 0 ? 500 : 600,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.2,
                          borderRight: cIdx < tableCols.length - 1 ? '1px solid #f8fafc' : 'none'
                        }}
                        title={row[cIdx] !== null && row[cIdx] !== undefined ? String(row[cIdx]) : ''}
                      >
                        {row[cIdx] === null || row[cIdx] === undefined ? '—' : String(row[cIdx])}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                ['Critical', 'High', 'Medium', 'Low', 'Info'].slice(0, limit && limit < 1000 ? limit : 5).map((cat: string, i: number) => (
                  <tr key={cat} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: cellPadding, color: '#334155', wordBreak: 'break-word' }}>{cat}</td>
                    <td style={{ padding: cellPadding, fontWeight: 600, color: accent, wordBreak: 'break-word' }}>{(5 - i) * 28}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: '#64748b' }}>
          <span>Showing {displayRows.length > 0 ? displayRows.length : 5} records</span>
          {limit && limit < 1000 && (
            <span style={{ fontSize: 9.5, opacity: 0.8 }}>Limit: {limit}</span>
          )}
        </Box>
      </Box>
    );
  }

  const isPie = chartType === 'pie';
  const isDonut = chartType === 'donut';
  const isPieOrDonut = isPie || isDonut;
  const isGauge = chartType === 'gauge';
  const isHorizontal = chartType === 'horizontal_bar';
  const isHeatmap = chartType === 'heatmap';
  const isNonAxis = isPieOrDonut || isGauge;

  const apexType: any = isHorizontal
    ? 'bar'
    : isGauge
      ? 'radialBar'
      : isPie
        ? 'pie'
        : isDonut
          ? 'donut'
          : (['bar', 'line', 'area', 'heatmap'].includes(chartType) ? chartType : 'bar');

  const isScalar = rawColumns.length <= 1 && (rawRows.length === 0 || rawRows[0]?.length <= 1);

  let categories: string[] = [];
  let series: any = [];
  let labels: string[] | undefined = undefined;

  if (isNonAxis) {
    if (isGauge) {
      const val = rawRows.length > 0 ? Number(rawRows[0]?.[1] ?? rawRows[0]?.[0] ?? 74) : 74;
      series = [Math.min(100, Math.max(0, isNaN(val) ? 74 : val))];
      labels = [visConfig?.y_axis_label || properties.yAxisLabel || 'Score'];
    } else {
      if (rawRows.length > 0) {
        categories = rawRows.map((r: any) => {
          if (Array.isArray(r)) {
            return r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0]);
          }
          if (typeof r === 'object' && r !== null) {
            const firstKey = Object.keys(r)[0];
            return r[firstKey] === null || r[firstKey] === undefined || r[firstKey] === '' ? '(Unassigned)' : String(r[firstKey]);
          }
          return String(r);
        });
        series = rawRows.map((r: any) => {
          if (Array.isArray(r)) {
            const v = Number(r[1] ?? r[0] ?? 0);
            return isNaN(v) ? 0 : v;
          }
          if (typeof r === 'object' && r !== null) {
            const keys = Object.keys(r);
            const valKey = keys[1] ?? keys[0];
            const v = Number(r[valKey] ?? 0);
            return isNaN(v) ? 0 : v;
          }
          const v = Number(r);
          return isNaN(v) ? 0 : v;
        });
      } else {
        categories = properties.chartFields && properties.chartFields.length > 0 ? properties.chartFields : ['Category A', 'Category B', 'Category C'];
        series = [45, 30, 25];
      }
      labels = categories;
    }
  } else {
    // Axis charts: Bar, Horizontal Bar, Line, Area, Heatmap
    if (rawRows.length === 0) {
      categories = ['No Data'];
      series = [{ name: visConfig?.y_axis_label || properties.yAxisLabel || 'Count', data: [0] }];
    } else if (isScalar) {
      categories = [rawColumns[0] || 'Total'];
      const val = Number(rawRows[0]?.[0] ?? 0);
      series = [{ name: rawColumns[0] || 'Total', data: [isNaN(val) ? 0 : val] }];
    } else if (isHeatmap) {
      categories = rawRows.map((r: any) => (r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0])));
      series = [{
        name: properties.yAxisLabel || 'Metric',
        data: rawRows.map((r: any) => ({
          x: r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0]),
          y: Number(r[1] ?? 0)
        }))
      }];
    } else {
      categories = rawRows.map((r: any) => (r[0] === null || r[0] === undefined || r[0] === '' ? '(Unassigned)' : String(r[0])));
      const metricLabel = visConfig?.y_axis_label || properties.yAxisLabel || rawColumns[1] || 'Count';
      const seriesData = rawRows.map((r: any) => {
        const v = Number(r[1] ?? r[0] ?? 0);
        return isNaN(v) ? 0 : v;
      });
      series = [{ name: metricLabel, data: seriesData }];
    }
  }

  const paletteColors = properties.colorPalette && COLOR_PALETTES_MAP[properties.colorPalette];
  const effectiveAccent = properties.accentColor || (paletteColors ? paletteColors[0] : (accent || '#3b82f6'));
  const chartColors = (properties.chartColors && properties.chartColors.length > 0)
    ? properties.chartColors
    : (paletteColors || (colors && colors.length > 0 ? colors : [effectiveAccent, '#38bdf8', '#818cf8', '#34d399', '#f43f5e', '#fbbf24', '#a855f7', '#06b6d4']));

  const apexOptions: any = {
    chart: {
      id: widget.id,
      type: apexType,
      toolbar: { show: false },
      animations: {
        enabled: !readOnly,
        dynamicAnimation: { enabled: !readOnly },
        animateGradually: { enabled: !readOnly },
      },
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    colors: chartColors,
    fill: {
      colors: chartColors,
      opacity: apexType === 'area' ? 0.35 : 1
    },
    stroke: {
      curve: properties.strokeCurve || 'smooth',
      width: chartType === 'line' || chartType === 'area' ? 2.5 : (isNonAxis ? 1 : 0),
    },
    legend: {
      show: properties.showLegend !== undefined ? properties.showLegend : true,
      position: isNonAxis ? 'bottom' : 'top',
      horizontalAlign: isNonAxis ? 'center' : 'right',
      fontSize: '10.5px',
      fontWeight: 500,
      markers: { size: 4 },
      itemMargin: { horizontal: 6, vertical: 2 },
      formatter: (seriesName: string, opts: any) => {
        if (isPieOrDonut && opts?.w?.globals?.series) {
          const val = opts.w.globals.series[opts.seriesIndex];
          if (val !== undefined && val !== null) {
            return `${seriesName}: ${typeof val === 'number' ? val.toLocaleString() : val}`;
          }
        }
        return seriesName;
      },
    },
    dataLabels: {
      enabled: properties.showDataLabels !== undefined ? Boolean(properties.showDataLabels) : (isPieOrDonut ? true : false),
      formatter: (val: any, opts: any) => {
        if (isPieOrDonut) {
          const count = opts?.w?.config?.series?.[opts?.seriesIndex] ?? opts?.w?.globals?.series?.[opts?.seriesIndex];
          if (count !== undefined && count !== null) {
            return `${typeof count === 'number' ? count.toLocaleString() : count}`;
          }
        }
        return typeof val === 'number' ? val.toLocaleString() : val;
      },
      style: { fontSize: '10px', fontWeight: 600 },
      dropShadow: { enabled: false },
    },
    tooltip: {
      theme: 'light',
      style: { fontSize: '11px' },
      y: {
        formatter: (val: any) => (typeof val === 'number' ? val.toLocaleString() : val),
      },
    },
  };

  if (isNonAxis) {
    if (labels) {
      apexOptions.labels = labels;
    }
    if (isDonut) {
      apexOptions.plotOptions = {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                formatter: (w: any) => {
                  const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                  return typeof total === 'number' ? total.toLocaleString() : total;
                },
              },
            },
          },
        },
      };
    } else if (isPie) {
      apexOptions.plotOptions = {
        pie: {
          expandOnClick: true,
        },
      };
    } else if (isGauge) {
      apexOptions.colors = [effectiveAccent];
      apexOptions.plotOptions = {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          hollow: { size: '60%' },
          track: {
            background: '#f1f5f9',
            strokeWidth: '100%'
          },
          dataLabels: {
            name: { show: true, fontSize: '11px', color: '#64748b' },
            value: { show: true, fontSize: '20px', fontWeight: 700, color: effectiveAccent },
          },
        },
      };
    }
  } else {
    // Axis charts ONLY
    apexOptions.grid = {
      show: properties.showGrid !== undefined ? properties.showGrid : true,
      borderColor: '#f1f5f9',
      strokeDashArray: 3,
    };
    if (apexType === 'bar') {
      apexOptions.plotOptions = {
        bar: {
          horizontal: isHorizontal,
          borderRadius: 3,
          columnWidth: '55%',
        },
      };
    }
    apexOptions.xaxis = {
      categories,
      title: {
        text: isHorizontal
          ? (visConfig?.y_axis_label || properties.yAxisLabel || '')
          : (visConfig?.x_axis_label || properties.xAxisLabel || ''),
        style: { fontSize: '10px', fontWeight: 600, color: '#64748b' },
      },
      labels: {
        style: { fontSize: '9.5px', colors: '#64748b' },
        rotate: -25,
        trim: true,
      },
    };
    apexOptions.yaxis = {
      title: {
        text: isHorizontal
          ? (visConfig?.x_axis_label || properties.xAxisLabel || '')
          : (visConfig?.y_axis_label || properties.yAxisLabel || ''),
        style: { fontSize: '10px', fontWeight: 600, color: '#64748b' },
      },
      labels: { style: { fontSize: '9.5px', colors: '#64748b' } },
    };
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
      <Chart
        key={`canvas-${widget.id}-${chartType}-${apexType}-${rawRows.length}-${rawColumns.length}`}
        options={apexOptions}
        series={series}
        type={apexType}
        height="100%"
        width="100%"
      />
    </Box>
  );
};

const FONT_OPTIONS = [
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Courier New', value: '"Courier New", monospace' }
];

const PRESET_TEXT_COLORS = [
  '#0f172a',
  '#ffffff',
  '#f97316',
  '#3b82f6',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#64748b'
];

interface CanvasFloatingTextToolbarProps {
  widget: WidgetItem;
  pageIndex?: number;
  totalPages?: number;
  onUpdateWidget?: (id: string, updates: Partial<WidgetItem['properties']>) => void;
  onDuplicateWidget?: (id: string) => void;
  onDeleteWidget?: (id: string) => void;
  onMoveWidgetToPage?: (widgetId: string, direction: 'prev' | 'next') => void;
}

const CanvasFloatingTextToolbar: React.FC<CanvasFloatingTextToolbarProps> = ({
  widget,
  pageIndex,
  totalPages,
  onUpdateWidget,
  onDuplicateWidget,
  onDeleteWidget,
  onMoveWidgetToPage
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const { type, properties = {} } = widget;
  const isHeading = type === 'heading';

  const fontSize = properties.fontSize || (isHeading ? 24 : 14);
  const fontWeight = properties.fontWeight || (isHeading ? 700 : 400);
  const isBold = Number(fontWeight) >= 600 || fontWeight === 'bold';
  const isItalic = properties.fontStyle === 'italic';
  const isUnderline = properties.textDecoration === 'underline';
  const isUppercase = properties.textTransform === 'uppercase';
  const textAlign = properties.textAlign || 'left';
  const currentColor = properties.color || (isHeading ? '#0f172a' : '#334155');
  const currentFont = properties.fontFamily || 'Inter, sans-serif';
  const lineHeight = properties.lineHeight || (isHeading ? 1.3 : 1.4);

  const handleFontSizeChange = (delta: number) => {
    const nextSize = Math.max(8, Math.min(96, Number(fontSize) + delta));
    onUpdateWidget?.(widget.id, { fontSize: nextSize });
  };

  const handleHeadingLevel = (level: 'h1' | 'h2' | 'h3') => {
    const sizeMap = { h1: 28, h2: 22, h3: 18 };
    onUpdateWidget?.(widget.id, {
      headingLevel: level,
      fontSize: sizeMap[level]
    });
  };

  const isAtTop = (widget.y || 0) <= 1;
  const isNearRightEdge = (widget.x || 0) >= 5 || ((widget.x || 0) + (widget.w || 4) >= 9);

  return (
    <div
      style={{
        position: 'absolute',
        top: isAtTop ? '6px' : '-44px',
        ...(isNearRightEdge ? { right: '0px' } : { left: '0px' }),
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: '#0f172a',
        padding: '3px 6px',
        borderRadius: '8px',
        border: '1px solid #334155',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.2)',
        color: '#f8fafc',
        userSelect: 'none',
        pointerEvents: 'auto',
        whiteSpace: 'nowrap'
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Heading Level Switcher */}
      {isHeading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', paddingRight: '4px', borderRight: '1px solid #334155' }}>
          {(['h1', 'h2', 'h3'] as const).map((lvl) => {
            const isLvlActive = (properties.headingLevel || 'h1') === lvl;
            return (
              <button
                key={lvl}
                type="button"
                onClick={() => handleHeadingLevel(lvl)}
                style={{
                  padding: '2px 5px',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '4px',
                  border: isLvlActive ? '1px solid #f97316' : '1px solid transparent',
                  backgroundColor: isLvlActive ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
                  color: isLvlActive ? '#f97316' : '#94a3b8',
                  cursor: 'pointer',
                  textTransform: 'uppercase'
                }}
              >
                {lvl}
              </button>
            );
          })}
        </div>
      )}

      {/* Font Family Quick Select */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setShowFontPicker(!showFontPicker)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 6px',
            fontSize: '11px',
            fontWeight: 500,
            borderRadius: '4px',
            border: '1px solid #334155',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            cursor: 'pointer',
            maxWidth: '90px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          <span>{FONT_OPTIONS.find((f) => currentFont.includes(f.label))?.label || 'Font'}</span>
          <ChevronDown size={11} color="#94a3b8" />
        </button>

        {showFontPicker && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
              padding: '4px',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              minWidth: '120px'
            }}
          >
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => {
                  onUpdateWidget?.(widget.id, { fontFamily: f.value });
                  setShowFontPicker(false);
                }}
                style={{
                  textAlign: 'left',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontFamily: f.value,
                  backgroundColor: currentFont === f.value ? 'rgba(249, 115, 22, 0.2)' : 'transparent',
                  color: currentFont === f.value ? '#f97316' : '#cbd5e1',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Font Size Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', backgroundColor: '#1e293b', borderRadius: '4px', border: '1px solid #334155', padding: '1px 3px' }}>
        <button
          type="button"
          onClick={() => handleFontSizeChange(-2)}
          style={{
            padding: '2px 4px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#94a3b8',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
          title="Decrease font size"
        >
          -
        </button>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#f8fafc', minWidth: '22px', textAlign: 'center' }}>
          {fontSize}
        </span>
        <button
          type="button"
          onClick={() => handleFontSizeChange(2)}
          style={{
            padding: '2px 4px',
            fontSize: '11px',
            fontWeight: 700,
            color: '#94a3b8',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
          title="Increase font size"
        >
          +
        </button>
      </div>

      {/* Text Color Picker Swatch */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setShowColorPicker(!showColorPicker)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 5px',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          title="Text Color"
        >
          <div
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '3px',
              backgroundColor: currentColor,
              border: '1px solid rgba(255,255,255,0.4)'
            }}
          />
          <ChevronDown size={10} color="#94a3b8" />
        </button>

        {showColorPicker && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
              padding: '6px',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              minWidth: '120px'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
              {PRESET_TEXT_COLORS.map((c) => (
                <div
                  key={c}
                  onClick={() => {
                    onUpdateWidget?.(widget.id, { color: c });
                    setShowColorPicker(false);
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border: currentColor === c ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                  }}
                />
              ))}
            </div>
            <input
              type="color"
              value={currentColor}
              onChange={(e) => onUpdateWidget?.(widget.id, { color: e.target.value })}
              style={{
                width: '100%',
                height: '22px',
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                borderRadius: '4px'
              }}
            />
          </div>
        )}
      </div>

      <div style={{ width: '1px', height: '16px', backgroundColor: '#334155', margin: '0 2px' }} />

      {/* Bold Toggle */}
      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { fontWeight: isBold ? 400 : 700 })}
        style={{
          padding: '3px 5px',
          fontSize: '11.5px',
          fontWeight: 800,
          borderRadius: '4px',
          border: isBold ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: isBold ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: isBold ? '#f97316' : '#cbd5e1',
          cursor: 'pointer'
        }}
        title="Bold"
      >
        B
      </button>

      {/* Italic Toggle */}
      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { fontStyle: isItalic ? 'normal' : 'italic' })}
        style={{
          padding: '3px 5px',
          fontSize: '11.5px',
          fontStyle: 'italic',
          fontWeight: 600,
          borderRadius: '4px',
          border: isItalic ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: isItalic ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: isItalic ? '#f97316' : '#cbd5e1',
          cursor: 'pointer'
        }}
        title="Italic"
      >
        I
      </button>

      {/* Underline Toggle */}
      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { textDecoration: isUnderline ? 'none' : 'underline' })}
        style={{
          padding: '3px 5px',
          fontSize: '11.5px',
          textDecoration: 'underline',
          fontWeight: 600,
          borderRadius: '4px',
          border: isUnderline ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: isUnderline ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: isUnderline ? '#f97316' : '#cbd5e1',
          cursor: 'pointer'
        }}
        title="Underline"
      >
        U
      </button>

      {/* Uppercase Toggle */}
      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { textTransform: isUppercase ? 'none' : 'uppercase' })}
        style={{
          padding: '3px 5px',
          fontSize: '10.5px',
          fontWeight: 700,
          borderRadius: '4px',
          border: isUppercase ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: isUppercase ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: isUppercase ? '#f97316' : '#cbd5e1',
          cursor: 'pointer'
        }}
        title="Uppercase"
      >
        aA
      </button>

      <div style={{ width: '1px', height: '16px', backgroundColor: '#334155', margin: '0 2px' }} />

      {/* Alignment Toggles */}
      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { textAlign: 'left' })}
        style={{
          padding: '3px 4px',
          borderRadius: '4px',
          border: textAlign === 'left' ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: textAlign === 'left' ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: textAlign === 'left' ? '#f97316' : '#94a3b8',
          cursor: 'pointer'
        }}
        title="Align Left"
      >
        <AlignLeft size={13} />
      </button>

      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { textAlign: 'center' })}
        style={{
          padding: '3px 4px',
          borderRadius: '4px',
          border: textAlign === 'center' ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: textAlign === 'center' ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: textAlign === 'center' ? '#f97316' : '#94a3b8',
          cursor: 'pointer'
        }}
        title="Align Center"
      >
        <AlignCenter size={13} />
      </button>

      <button
        type="button"
        onClick={() => onUpdateWidget?.(widget.id, { textAlign: 'right' })}
        style={{
          padding: '3px 4px',
          borderRadius: '4px',
          border: textAlign === 'right' ? '1px solid #f97316' : '1px solid transparent',
          backgroundColor: textAlign === 'right' ? 'rgba(249, 115, 22, 0.25)' : 'transparent',
          color: textAlign === 'right' ? '#f97316' : '#94a3b8',
          cursor: 'pointer'
        }}
        title="Align Right"
      >
        <AlignRight size={13} />
      </button>

      <div style={{ width: '1px', height: '16px', backgroundColor: '#334155', margin: '0 2px' }} />

      {/* Auto-Fit Height Action */}
      <button
        type="button"
        onClick={() => {
          const text = String(properties.content || '');
          const lines = text.split('\n');
          let totalLines = 0;
          const approxCharsPerLine = Math.max(10, Math.floor(((widget.w || 6) * 58) / (Number(fontSize) * 0.55)));
          lines.forEach(l => {
            totalLines += Math.max(1, Math.ceil((l.length || 1) / approxCharsPerLine));
          });
          const estHeightPx = totalLines * (Number(fontSize) * (typeof lineHeight === 'number' ? lineHeight : 1.4)) + 24;
          const requiredRows = Math.min(24, Math.max(isHeading ? 2 : 2, Math.ceil(estHeightPx / 40)));
          onUpdateWidget?.(widget.id, { h: requiredRows } as any);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '3px 5px',
          borderRadius: '4px',
          border: '1px solid #334155',
          backgroundColor: '#1e293b',
          color: '#38bdf8',
          fontSize: '10.5px',
          fontWeight: 600,
          cursor: 'pointer'
        }}
        title="Auto-fit Widget Height to Text Content"
      >
        <Maximize2 size={11} />
        Auto-fit
      </button>

      {/* Move to Previous / Next Page Actions */}
      {onMoveWidgetToPage && (pageIndex !== undefined && pageIndex > 0) && (
        <button
          type="button"
          onClick={() => onMoveWidgetToPage(widget.id, 'prev')}
          style={{
            padding: '3px 4px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer'
          }}
          title="Move to Previous Page"
          onMouseEnter={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
        >
          <ArrowUp size={13} />
        </button>
      )}
      {onMoveWidgetToPage && (
        <button
          type="button"
          onClick={() => onMoveWidgetToPage(widget.id, 'next')}
          style={{
            padding: '3px 4px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer'
          }}
          title={pageIndex !== undefined && totalPages !== undefined && pageIndex < totalPages - 1 ? "Move to Next Page" : "Move to New Page"}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
        >
          <ArrowDown size={13} />
        </button>
      )}

      {/* Duplicate & Delete Actions */}
      {onDuplicateWidget && (
        <button
          type="button"
          onClick={() => onDuplicateWidget(widget.id)}
          style={{
            padding: '3px 4px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#94a3b8',
            cursor: 'pointer'
          }}
          title="Duplicate Element"
        >
          <Copy size={13} />
        </button>
      )}

      {onDeleteWidget && (
        <button
          type="button"
          onClick={() => onDeleteWidget(widget.id)}
          style={{
            padding: '3px 4px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#ef4444',
            cursor: 'pointer'
          }}
          title="Delete Element"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
};

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

interface CanvasInlineTextEditorProps {
  widget: WidgetItem;
  readOnly?: boolean;
  onUpdateWidget?: (id: string, updates: Partial<WidgetItem['properties']>) => void;
}

const CanvasInlineTextEditor: React.FC<CanvasInlineTextEditorProps> = ({
  widget,
  readOnly = false,
  onUpdateWidget
}) => {
  const { type, properties = {} } = widget;
  const isHeading = type === 'heading';

  const [localText, setLocalText] = useState(properties.content ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setLocalText(properties.content ?? '');
    }
  }, [properties.content, isFocused]);

  const checkOverflow = () => {
    if (textareaRef.current) {
      const hasOverflow = textareaRef.current.scrollHeight > textareaRef.current.clientHeight + 4;
      setIsOverflowing(hasOverflow);
    }
  };

  useEffect(() => {
    checkOverflow();
  }, [localText, properties.fontSize, properties.fontWeight, widget.h, widget.w]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setLocalText(val);
    onUpdateWidget?.(widget.id, { content: val });
  };

  const handleBlur = () => {
    setIsFocused(false);
    onUpdateWidget?.(widget.id, { content: localText });
    checkOverflow();
  };

  const handleAutoExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (textareaRef.current) {
      const scrollHeight = textareaRef.current.scrollHeight;
      const neededRows = Math.min(24, Math.max(isHeading ? 2 : 2, Math.ceil((scrollHeight + 18) / 40)));
      onUpdateWidget?.(widget.id, { h: neededRows } as any);
      setIsOverflowing(false);
    }
  };

  const bgVal = properties.backgroundColor && properties.backgroundColor !== 'transparent' ? properties.backgroundColor : undefined;
  const hasBorder = Boolean(properties.borderWidth && Number(properties.borderWidth) > 0 && properties.borderColor);
  const hasLeftAccent = Boolean(properties.borderLeftWidth && Number(properties.borderLeftWidth) > 0 && properties.borderLeftColor);

  const fontSize = properties.fontSize || (isHeading ? 24 : 14);
  const fontWeight = properties.fontWeight || (isHeading ? 'bold' : 'normal');
  const fontStyle = properties.fontStyle || 'normal';
  const textDecoration = properties.textDecoration || 'none';
  const textTransform = (properties.textTransform as any) || 'none';
  const letterSpacing = properties.letterSpacing !== undefined ? `${properties.letterSpacing}px` : undefined;
  const color = properties.color || (isHeading ? '#0f172a' : '#334155');
  const textAlign = properties.textAlign || 'left';
  const lineHeight = properties.lineHeight || (isHeading ? 1.3 : 1.5);
  const fontFamily = properties.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  if (readOnly) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: isHeading ? 'center' : 'flex-start',
          justifyContent: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start'),
          bgcolor: bgVal || 'transparent',
          borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : 0,
          border: hasBorder ? `${properties.borderWidth}px ${properties.dividerStyle || properties.borderStyle || 'solid'} ${properties.borderColor}` : 'none',
          borderLeft: hasLeftAccent ? `${properties.borderLeftWidth}px ${properties.borderLeftStyle || 'solid'} ${properties.borderLeftColor}` : undefined,
          p: properties.padding !== undefined ? `${properties.padding}px` : (hasBorder || hasLeftAccent || bgVal ? '6px 10px' : 0),
          boxShadow: 'none',
          boxSizing: 'border-box',
          overflow: 'visible'
        }}
      >
        <Typography
          sx={{
            fontSize,
            fontWeight,
            fontStyle,
            textDecoration,
            textTransform,
            letterSpacing,
            color,
            textAlign,
            fontFamily,
            width: '100%',
            lineHeight,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflow: 'visible',
            boxShadow: 'none'
          }}
        >
          {properties.content ?? ''}
        </Typography>
      </Box>
    );
  }

  const placeholderText = isHeading ? 'Type heading here...' : 'Type text here...';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: isHeading ? 'center' : 'flex-start',
        justifyContent: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start'),
        bgcolor: bgVal || 'transparent',
        borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : 0,
        border: hasBorder ? `${properties.borderWidth}px ${properties.dividerStyle || properties.borderStyle || 'solid'} ${properties.borderColor}` : 'none',
        borderLeft: hasLeftAccent ? `${properties.borderLeftWidth}px ${properties.borderLeftStyle || 'solid'} ${properties.borderLeftColor}` : undefined,
        p: properties.padding !== undefined ? `${properties.padding}px` : (hasBorder || hasLeftAccent || bgVal ? '6px 10px' : 0),
        position: 'relative',
        cursor: 'text',
        boxShadow: 'none',
        boxSizing: 'border-box'
      }}
      onClick={() => {
        textareaRef.current?.focus();
      }}
    >
      <textarea
        ref={textareaRef}
        value={localText}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (isHeading && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            textareaRef.current?.blur();
          }
        }}
        placeholder={placeholderText}
        style={{
          width: '100%',
          height: isHeading ? 'auto' : '100%',
          maxHeight: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'none',
          fontFamily,
          fontSize: `${fontSize}px`,
          fontWeight: fontWeight as any,
          fontStyle,
          textDecoration,
          textTransform,
          letterSpacing,
          color,
          textAlign: textAlign as any,
          lineHeight: typeof lineHeight === 'number' ? `${lineHeight}` : lineHeight,
          padding: '0',
          margin: '0',
          boxSizing: 'border-box',
          overflow: isHeading ? 'hidden' : 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          cursor: 'text'
        }}
      />

      {/* Overflow Warning Badge with 1-Click Auto Expand */}
      {!readOnly && isOverflowing && (
        <Box
          onClick={handleAutoExpand}
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            zIndex: 30,
            bgcolor: '#f59e0b',
            color: '#0f172a',
            fontSize: 9.5,
            fontWeight: 700,
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            userSelect: 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: '#fbbf24',
              transform: 'scale(1.05)'
            }
          }}
          title="Text overflows container height. Click to expand box."
        >
          <Maximize2 size={10} />
          Expand Box
        </Box>
      )}
    </Box>
  );
};

interface CanvasImageWidgetProps {
  widget: WidgetItem;
  activeReportId?: string;
  readOnly?: boolean;
  isSelected?: boolean;
  onUpdateWidget?: (id: string, updates: Partial<WidgetItem['properties']>) => void;
}

const CanvasImageWidget: React.FC<CanvasImageWidgetProps> = ({
  widget,
  activeReportId,
  readOnly = false,
  isSelected = false,
  onUpdateWidget
}) => {
  const { properties = {} } = widget;
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imgUrl = !imageError ? (properties.image_url || (properties.image_name && properties.image_name.startsWith('data:') ? properties.image_name : '') || (properties.image_key ? `/api/${properties.image_key}` : '')) : '';
  const imgFit = (properties.imageFit as any) || 'contain';
  const imgAlign = properties.textAlign === 'left' ? 'flex-start' : (properties.textAlign === 'right' ? 'flex-end' : 'center');

  // Reset image error if url changed
  useEffect(() => {
    setImageError(false);
  }, [properties.image_url, properties.image_key, properties.image_name]);

  const processFile = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Invalid file format. Please upload an image file (PNG, JPG, SVG, WEBP, GIF).');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showError(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 5MB.`);
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      let uploadedData: any = null;
      if (activeReportId) {
        try {
          const res = await baseapi.post(`custom_reports/${activeReportId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedData = res.data?.data || res.data;
        } catch (apiErr: any) {
          console.warn('API upload failed, falling back to local data preview', apiErr);
        }
      }

      const imageName = uploadedData?.image_name || file.name;
      const originalName = uploadedData?.original_name || file.name;
      const imageKey = uploadedData?.image_key || '';
      const size = uploadedData?.size || file.size;
      const contentType = uploadedData?.content_type || file.type;
      const finalUrl = uploadedData?.url || uploadedData?.image_url || (imageKey ? `/api/${imageKey}` : previewUrl);

      setImageError(false);
      onUpdateWidget?.(widget.id, {
        image_name: imageName,
        original_name: originalName,
        image_key: imageKey,
        size: size,
        content_type: contentType,
        image_url: finalUrl,
        imageFit: properties.imageFit || 'contain'
      });

      showSuccess(`Uploaded image: ${originalName}`);
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Image upload error, applied local preview');
      setImageError(false);
      onUpdateWidget?.(widget.id, {
        image_name: file.name,
        original_name: file.name,
        image_url: previewUrl,
        imageFit: properties.imageFit || 'contain'
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (readOnly) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageError(false);
    onUpdateWidget?.(widget.id, {
      image_url: undefined,
      image_key: undefined,
      image_name: undefined,
      original_name: undefined,
      size: undefined,
      content_type: undefined
    });
  };

  if (readOnly) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: imgAlign,
          bgcolor: 'transparent',
          borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : 0,
          overflow: 'hidden'
        }}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={properties.original_name || properties.image_name || "Image"}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: imgFit === 'fill' ? '100%' : 'auto',
              height: imgFit === 'fill' || imgFit === 'cover' ? '100%' : 'auto',
              objectFit: imgFit as any,
              borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : undefined,
              display: 'block'
            }}
            onError={() => setImageError(true)}
          />
        ) : null}
      </Box>
    );
  }

  // Uploading state
  if (isUploading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: '#f8fafc',
          border: '2px dashed #f97316',
          borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : '8px',
          p: 2
        }}
      >
        <Loader2 size={24} className="animate-spin text-orange-500" style={{ animation: 'spin 1s linear infinite' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#f97316' }}>Uploading image...</Typography>
        <Typography sx={{ fontSize: 10, color: '#64748b' }}>Processing file (Max 5MB)</Typography>
      </Box>
    );
  }

  // Resting / Empty State
  if (!imgUrl) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isDragOver ? 'rgba(249, 115, 22, 0.08)' : '#f8fafc',
          border: isDragOver ? '2px dashed #f97316' : '2px dashed #cbd5e1',
          borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : '8px',
          p: 1.5,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxSizing: 'border-box',
          '&:hover': {
            borderColor: '#f97316',
            bgcolor: 'rgba(249, 115, 22, 0.04)'
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
        }}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
          onChange={handleFileChange}
        />
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: isDragOver ? '#ffedd5' : '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDragOver ? '#f97316' : '#64748b',
            mb: 0.5,
            transition: 'all 0.2s ease'
          }}
        >
          <UploadCloud size={20} />
        </Box>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#334155', textAlign: 'center', lineHeight: 1.2 }}>
          Upload Image
        </Typography>
        <Typography sx={{ fontSize: 9.5, color: '#64748b', textAlign: 'center', mt: 0.25 }}>
          Click or drag image here
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            px: 0.75,
            py: 0.2,
            bgcolor: '#e2e8f0',
            borderRadius: '8px',
            fontSize: 8.5,
            fontWeight: 600,
            color: '#475569',
            letterSpacing: 0.2
          }}
        >
          Max 5MB • PNG, JPG, WEBP, SVG
        </Box>
      </Box>
    );
  }

  // Uploaded Image State
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: imgAlign,
        position: 'relative',
        borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : 0,
        overflow: 'hidden'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
        onChange={handleFileChange}
      />

      <img
        src={imgUrl}
        alt={properties.original_name || properties.image_name || "Image"}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: imgFit === 'fill' ? '100%' : 'auto',
          height: imgFit === 'fill' || imgFit === 'cover' ? '100%' : 'auto',
          objectFit: imgFit as any,
          borderRadius: properties.borderRadius ? `${properties.borderRadius}px` : undefined,
          display: 'block'
        }}
        onError={() => setImageError(true)}
      />

      {/* Drag Over Overlay for Replacement */}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(249, 115, 22, 0.88)',
            color: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            zIndex: 10
          }}
        >
          <UploadCloud size={24} />
          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Drop image to replace (Max 5MB)</Typography>
        </Box>
      )}

      {/* Quick Action Overlay on Hover or Selected */}
      {!isDragOver && (isHovered || isSelected) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            display: 'flex',
            gap: 0.5,
            zIndex: 20,
            bgcolor: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(4px)',
            p: '3px 6px',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            title="Replace Image (Max 5MB)"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#f97316',
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Upload size={11} />
            <span>Replace</span>
          </button>
          <button
            type="button"
            title="Remove Image"
            onClick={handleRemoveImage}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px 5px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: 'rgba(239, 68, 68, 0.25)',
              color: '#f87171',
              cursor: 'pointer'
            }}
          >
            <Trash2 size={11} />
          </button>
        </Box>
      )}
    </Box>
  );
};

const ReportCanvas: React.FC<ReportCanvasProps> = ({
  pages,
  activePageId,
  selectedId,
  globalSettings,
  readOnly = false,
  activeReportId,
  onSelectWidget,
  onLayoutChange,
  onAddPage,
  onDuplicateWidget,
  onDeleteWidget,
  onDropWidget,
  onUpdateWidget,
  onMoveWidgetToPage
}) => {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [isDragOverPageId, setIsDragOverPageId] = useState<string | null>(null);

  // Page Geometry (Supports Portrait, Landscape, A4 & Letter)
  const pageMargin = globalSettings?.pageMargin !== undefined ? globalSettings.pageMargin : 24;
  const pageBgColor = globalSettings?.backgroundColor || '#ffffff';
  const showGridGuides = !readOnly && globalSettings?.showGridGuides !== false;

  const isLandscape = globalSettings?.orientation === 'landscape';
  const isLetter = globalSettings?.pageFormat === 'Letter';
  const pageWidth = isLandscape ? (isLetter ? 1056 : 1123) : (isLetter ? 816 : 794);
  const pageHeight = isLandscape ? (isLetter ? 816 : 794) : (isLetter ? 1056 : 1123);
  const printWidth = isLandscape ? (isLetter ? '11in' : '297mm') : (isLetter ? '8.5in' : '210mm');
  const printHeight = isLandscape ? (isLetter ? '8.5in' : '210mm') : (isLetter ? '11in' : '297mm');
  const printPageSize = `${isLetter ? 'letter' : 'A4'} ${isLandscape ? 'landscape' : 'portrait'}`;

  const gridWidth = pageWidth - (pageMargin * 2);
  const maxGridRows = isLandscape ? 16 : 24;

  // Close 3-dots menu when selection changes or clicking outside
  useEffect(() => {
    setMenuOpenId(null);
  }, [selectedId]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setMenuOpenId(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const renderWidgetContent = (widget: WidgetItem, pageIndex: number, totalPages: number) => {
    if (!widget) return null;
    const { type, properties = {} } = widget;

    switch (type) {
      case 'heading':
      case 'text':
        return (
          <CanvasInlineTextEditor
            widget={widget}
            readOnly={readOnly}
            onUpdateWidget={onUpdateWidget}
          />
        );
      case 'divider': {
        const divColor = properties.color || properties.borderColor || '#e2e8f0';
        const divThickness = properties.borderWidth !== undefined ? Number(properties.borderWidth) : 1;
        const divStyle = properties.dividerStyle || properties.borderStyle || 'solid';
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', px: 0.5, boxSizing: 'border-box' }}>
            <Box
              component="div"
              sx={{
                width: '100%',
                height: 0,
                borderTop: `${divThickness}px ${divStyle} ${divColor}`,
                borderBottom: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                boxSizing: 'border-box'
              }}
            />
          </Box>
        );
      }
      case 'header': {
        const textAlign = properties.textAlign || 'left';
        const logoAlign = properties.logoAlign || 'left';
        const headerImageUrl = properties.image_url || (properties.image_key ? `/api/${properties.image_key}` : '') || '';
        const showLogo = logoAlign !== 'none' && Boolean(headerImageUrl);
        const logoHeight = properties.logoHeight || 28;

        const headerTitle = properties.content !== undefined
          ? properties.content
          : (globalSettings?.headerProperties?.content !== undefined ? globalSettings.headerProperties.content : '');
        const headerSubtitle = properties.headerSubtitle !== undefined
          ? properties.headerSubtitle
          : (globalSettings?.headerProperties?.headerSubtitle !== undefined ? globalSettings.headerProperties.headerSubtitle : '');

        const renderLogoNode = () => {
          if (!showLogo) return null;
          return (
            <img
              src={headerImageUrl}
              alt={properties.original_name || properties.image_name || "Logo"}
              style={{
                height: `${logoHeight}px`,
                maxWidth: '160px',
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                (e.target as HTMLElement).style.display = 'none';
              }}
            />
          );
        };

        const renderTitleNode = () => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              textAlign: textAlign,
              alignItems: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start')
            }}
          >
            {headerTitle && (
              <Typography
                sx={{
                  fontSize: properties.fontSize || 13,
                  fontWeight: properties.fontWeight || 'bold',
                  color: properties.color || '#0f172a',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {headerTitle}
              </Typography>
            )}
            {headerSubtitle && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: '#64748b',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {headerSubtitle}
              </Typography>
            )}
          </Box>
        );

        const borderWidth = properties.borderWidth !== undefined ? properties.borderWidth : 1;
        const bottomBorder = borderWidth > 0
          ? `${borderWidth}px ${properties.dividerStyle || 'solid'} ${properties.borderColor || '#e2e8f0'}`
          : 'none';

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              alignItems: 'center',
              borderBottom: bottomBorder,
              bgcolor: properties.backgroundColor || 'transparent',
              pb: borderWidth > 0 ? 0.75 : 0,
              px: 0.5,
              boxSizing: 'border-box'
            }}
          >
            {/* Left Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {logoAlign === 'left' && renderLogoNode()}
              {textAlign === 'left' && renderTitleNode()}
            </Box>

            {/* Center Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {logoAlign === 'center' && renderLogoNode()}
              {textAlign === 'center' && renderTitleNode()}
            </Box>

            {/* Right Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {textAlign === 'right' && renderTitleNode()}
              {logoAlign === 'right' && renderLogoNode()}
            </Box>
          </Box>
        );
      }
      case 'footer': {
        const textAlign = properties.textAlign || 'left';
        const paginationAlign = properties.paginationAlign || 'right';
        const showPagination = paginationAlign !== 'none';

        let paginationText = `Page ${pageIndex + 1} of ${totalPages}`;
        if (properties.paginationFormat === 'slash') {
          paginationText = `${pageIndex + 1} / ${totalPages}`;
        } else if (properties.paginationFormat === 'page_only') {
          paginationText = `Page ${pageIndex + 1}`;
        } else if (properties.paginationFormat === 'number_only') {
          paginationText = `${pageIndex + 1}`;
        }

        const footerText = properties.content !== undefined ? properties.content : '';

        const renderTextNode = () => footerText ? (
          <Typography
            sx={{
              fontSize: properties.fontSize || 12,
              color: properties.color || '#64748b',
              fontWeight: properties.fontWeight || 'normal',
              lineHeight: 1.2
            }}
          >
            {footerText}
          </Typography>
        ) : null;

        const renderPaginationNode = () => showPagination ? (
          <Typography
            sx={{
              fontSize: properties.fontSize || 12,
              color: properties.color || '#64748b',
              fontWeight: properties.fontWeight || 'normal',
              lineHeight: 1.2
            }}
          >
            {paginationText}
          </Typography>
        ) : null;

        const borderWidth = properties.borderWidth !== undefined ? properties.borderWidth : 1;
        const topBorder = borderWidth > 0
          ? `${borderWidth}px ${properties.dividerStyle || 'solid'} ${properties.borderColor || '#e2e8f0'}`
          : 'none';

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              alignItems: 'center',
              borderTop: topBorder,
              bgcolor: properties.backgroundColor || 'transparent',
              pt: borderWidth > 0 ? 0.75 : 0,
              px: 0.5,
              boxSizing: 'border-box'
            }}
          >
            {/* Left Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {textAlign === 'left' && renderTextNode()}
              {paginationAlign === 'left' && renderPaginationNode()}
            </Box>

            {/* Center Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {textAlign === 'center' && renderTextNode()}
              {paginationAlign === 'center' && renderPaginationNode()}
            </Box>

            {/* Right Slot */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
              {textAlign === 'right' && renderTextNode()}
              {paginationAlign === 'right' && renderPaginationNode()}
            </Box>
          </Box>
        );
      }
      case 'chart':
      case 'bar':
      case 'horizontal_bar':
      case 'line':
      case 'area':
      case 'pie':
      case 'donut':
      case 'gauge':
      case 'metric':
      case 'table':
      case 'heatmap': {
        const pal = properties.colorPalette && COLOR_PALETTES_MAP[properties.colorPalette];
        const accent = properties.accentColor || (pal ? pal[0] : '#3b82f6');
        const colors = properties.chartColors && properties.chartColors.length > 0
          ? properties.chartColors
          : (pal || [accent, '#38bdf8', '#818cf8', '#34d399', '#f43f5e', '#fbbf24', '#a855f7', '#06b6d4']);

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'transparent',
              p: 0.5
            }}
          >
            {(properties.chartTitle || properties.title || properties.name) && (
              <Typography sx={{ fontSize: properties.fontSize || 13, fontWeight: properties.fontWeight || 'bold', mb: 0.5, color: properties.color || '#0f172a' }}>
                {properties.chartTitle || properties.title || properties.name}
              </Typography>
            )}
            <ReportChartRenderer
              widget={widget}
              accent={accent}
              colors={colors}
              properties={properties}
              readOnly={readOnly}
            />
          </Box>
        );
      }
      case 'image':
        return (
          <CanvasImageWidget
            widget={widget}
            activeReportId={activeReportId}
            readOnly={readOnly}
            isSelected={selectedId === widget.id}
            onUpdateWidget={onUpdateWidget}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      className="report-canvas-root"
      sx={{
        flex: 1,
        overflowY: 'auto',
        bgcolor: readOnly ? '#f8fafc' : '#0f172a', // Light background on readOnly, slate-900 in editor
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: readOnly ? 2 : 4,
        gap: readOnly ? 2 : 4,
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: readOnly ? 'transparent' : 'rgba(15, 23, 42, 0.6)'
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: readOnly ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.25)',
          borderRadius: '4px',
          '&:hover': {
            bgcolor: 'rgba(148, 163, 184, 0.6)'
          },
          '&:active': {
            bgcolor: '#f97316'
          }
        }
      }}
      onClick={() => {
        if (!readOnly) onSelectWidget(null);
      }}
    >
      {/* Custom styles for React Grid Layout dragging, placeholder, and handles */}
      <style>{`
        @page {
          size: ${printPageSize};
          margin: 0mm;
        }
        @media print {
          html, body, #root {
            width: ${printWidth} !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background-color: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .report-canvas-root {
            padding: 0 !important;
            margin: 0 !important;
            gap: 0 !important;
            background: transparent !important;
            background-color: #ffffff !important;
            overflow: visible !important;
            display: block !important;
            width: ${printWidth} !important;
          }
          .report-page-wrapper {
            margin: 0 !important;
            padding: 0 !important;
            gap: 0 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            display: block !important;
            width: ${printWidth} !important;
          }
          .report-page-wrapper:not(:last-child) {
            page-break-after: always !important;
            break-after: page !important;
          }
          .report-page-wrapper:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          .report-page-container {
            width: ${printWidth} !important;
            height: ${printHeight} !important;
            min-height: ${printHeight} !important;
            max-height: ${printHeight} !important;
            margin: 0 !important;
            box-shadow: none !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            overflow: hidden !important;
          }
          button {
            display: none !important;
          }
        }

        .react-grid-item.react-grid-placeholder {
          background: rgba(249, 115, 22, 0.12) !important;
          border: 2px dashed #f97316 !important;
          border-radius: 8px !important;
          opacity: 1 !important;
          z-index: 2 !important;
          transition: transform 120ms ease, width 120ms ease, height 120ms ease !important;
        }

        .react-grid-item.react-draggable-dragging {
          background-color: #ffffff !important;
          box-shadow: 0 20px 35px -8px rgba(0, 0, 0, 0.22), 0 10px 18px -6px rgba(0, 0, 0, 0.12) !important;
          border: 2px solid #f97316 !important;
          border-radius: 8px !important;
          z-index: 100 !important;
          cursor: grabbing !important;
          opacity: 0.98 !important;
        }

        .react-grid-item.resizing {
          background-color: #ffffff !important;
          box-shadow: 0 12px 28px -6px rgba(0, 0, 0, 0.18) !important;
          border: 2px solid #f97316 !important;
          border-radius: 8px !important;
          z-index: 90 !important;
        }

        .react-grid-item > .react-resizable-handle {
          z-index: 60;
        }

        .react-grid-item > .react-resizable-handle-se::after {
          border-right: 2px solid #f97316 !important;
          border-bottom: 2px solid #f97316 !important;
          width: 7px !important;
          height: 7px !important;
          right: 4px !important;
          bottom: 4px !important;
        }
      `}</style>

      {(pages || []).map((page, index) => {
        if (!page) return null;
        const pageId = page.id || `page-${index}`;
        const isLastPage = index === pages.length - 1;
        const widgets = Array.isArray(page.widgets) ? page.widgets.filter(Boolean) : [];
        const headerWidget = widgets.find(w => w && w.type === 'header');
        const footerWidget = widgets.find(w => w && w.type === 'footer');
        const bodyWidgets = widgets.filter(w => w && w.type !== 'header' && w.type !== 'footer');

        const layout = bodyWidgets.map((w, wIdx) => ({
          i: w.id || `w-${wIdx}`,
          x: Number.isFinite(w.x) ? w.x : 0,
          y: Number.isFinite(w.y) ? w.y : 0,
          w: w.w || 4,
          h: w.h || 4,
          minW: w.type === 'divider' ? 3 : (w.type === 'heading' ? 3 : 2),
          minH: w.type === 'divider' ? 1 : (w.type === 'heading' ? 1 : 2),
          maxH: maxGridRows
        }));

        const isActive = pageId === activePageId;
        const effectiveHeaderProps = headerWidget?.properties || globalSettings?.headerProperties || {};
        const isHeaderEnabled = headerWidget ? true : (globalSettings?.enableHeader !== false);
        const showHeaderOnPage = isHeaderEnabled && (!effectiveHeaderProps.skipOnFirstPage || index > 0);

        const effectiveFooterProps = footerWidget?.properties || globalSettings?.footerProperties || {};
        const isFooterEnabled = footerWidget ? true : (globalSettings?.enableFooter !== false);

        return (
          <Box
            key={pageId}
            id={`report-page-${pageId}`}
            className="report-page-wrapper"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: readOnly ? 0 : 1,
              pageBreakAfter: (!isLastPage && readOnly) ? 'always' : 'auto',
              breakAfter: (!isLastPage && readOnly) ? 'page' : 'auto'
            }}
          >
            {!readOnly && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Page {index + 1}</Typography>
            )}
            <Box
              className="report-page-container"
              sx={{
                width: pageWidth,
                height: pageHeight,
                p: `${pageMargin}px`,
                boxSizing: 'border-box',
                overflow: 'hidden',
                bgcolor: pageBgColor,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                backgroundImage: (!readOnly && isDragOverPageId === page.id)
                  ? 'radial-gradient(circle, #cbd5e1 1.2px, transparent 1.2px)'
                  : (!readOnly && isActive && showGridGuides)
                    ? 'radial-gradient(circle, #f1f5f9 1px, transparent 1px)'
                    : 'none',
                backgroundSize: '16px 16px',
                backgroundPosition: `${pageMargin}px ${pageMargin}px`,
                color: '#0f172a',
                boxShadow: readOnly
                  ? '0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.04)'
                  : (isActive ? '0 0 0 2px #f97316, 0 8px 24px -4px rgba(0, 0, 0, 0.5)' : '0 4px 12px -2px rgba(0, 0, 0, 0.5)'),
                outline: (!readOnly && isDragOverPageId === page.id) ? '2px dashed #f97316' : 'none',
                outlineOffset: '-2px',
                position: 'relative',
                transition: 'box-shadow 0.2s, outline 0.2s, width 0.2s, height 0.2s',
                cursor: (readOnly || isActive) ? 'default' : 'pointer'
              }}
              onClick={(e) => {
                if (readOnly) return;
                e.stopPropagation();
                onSelectWidget(null, page.id);
              }}
              onDragOver={(e) => {
                if (readOnly) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                if (isDragOverPageId !== page.id) {
                  setIsDragOverPageId(page.id);
                }
              }}
              onDragLeave={(e) => {
                if (readOnly) return;
                if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) {
                  setIsDragOverPageId(null);
                }
              }}
              onDrop={(e) => {
                if (readOnly) return;
                e.preventDefault();
                setIsDragOverPageId(null);
                const widgetType = e.dataTransfer.getData('text/plain') as WidgetType;
                if (!widgetType) return;

                const rect = e.currentTarget.getBoundingClientRect();
                const dropX = e.clientX - rect.left - pageMargin;
                const dropY = e.clientY - rect.top - pageMargin;

                const COLS = 12;
                const MARGIN_X = 8;
                const MARGIN_Y = 8;
                const ROW_HEIGHT = 32;
                const colWidth = (gridWidth - (COLS - 1) * MARGIN_X) / COLS;
                const colStep = colWidth + MARGIN_X;
                const rowStep = ROW_HEIGHT + MARGIN_Y;

                const gridX = Math.max(0, Math.min(COLS - 1, Math.floor(dropX / colStep)));
                const gridY = Math.max(0, Math.min(maxGridRows - 1, Math.floor(dropY / rowStep)));

                onDropWidget?.(widgetType, page.id, { x: gridX, y: gridY });
              }}
            >
              {/* Document Watermark Overlay */}
              {(() => {
                const ws = globalSettings?.watermarkSettings;
                const isEnabled = Boolean(globalSettings?.enableWatermark || ws?.enabled);
                if (!isEnabled) return null;
                if (ws?.skipOnFirstPage && index === 0) return null;
                const text = (ws?.text || '').trim();
                if (!text) return null;

                const fontSize = ws?.fontSize || 54;
                const color = ws?.color || '#94a3b8';
                const opacity = ws?.opacity !== undefined ? ws.opacity : 0.12;
                const rotation = ws?.rotation !== undefined ? ws.rotation : -45;

                return (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 50,
                      overflow: 'hidden',
                      userSelect: 'none'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: `${fontSize}px`,
                        fontWeight: 800,
                        color: color,
                        opacity: opacity,
                        transform: `rotate(${rotation}deg)`,
                        textTransform: 'uppercase',
                        letterSpacing: '6px',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                );
              })()}

              {/* Pinned Top Header Slot */}
              {showHeaderOnPage && (
                <Box
                  sx={{
                    width: '100%',
                    height: 38,
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 2,
                    mb: 1,
                    boxSizing: 'border-box',
                    border: readOnly
                      ? 'none'
                      : ((headerWidget && selectedId === headerWidget.id) ? '2px solid #f97316' : '1px solid transparent'),
                    borderRadius: '6px',
                    p: '2px 4px',
                    cursor: readOnly ? 'default' : 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: (!readOnly && headerWidget && selectedId === headerWidget.id)
                      ? '0 4px 14px rgba(249, 115, 22, 0.15)'
                      : 'none',
                    '&:hover': {
                      borderColor: (!readOnly && headerWidget && selectedId === headerWidget.id) ? '#f97316' : (!readOnly ? '#cbd5e1' : 'transparent')
                    }
                  }}
                  onClick={(e) => {
                    if (readOnly) return;
                    e.stopPropagation();
                    if (headerWidget) {
                      onSelectWidget(headerWidget.id, page.id);
                    } else {
                      onSelectWidget(null, page.id);
                    }
                  }}
                >
                  {/* Floating Action Menu for Selected Header */}
                  {!readOnly && headerWidget && selectedId === headerWidget.id && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-28px',
                        right: '0px',
                        zIndex: 100,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        backgroundColor: '#ffffff',
                        padding: '3px 4px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                        pointerEvents: 'auto'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          title="Header Actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(menuOpenId === headerWidget.id ? null : headerWidget.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 3px',
                            borderRadius: '3px',
                            color: menuOpenId === headerWidget.id ? '#ea580c' : '#475569',
                            backgroundColor: menuOpenId === headerWidget.id ? '#ffedd5' : 'transparent',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <MoreHorizontal size={14} />
                        </button>

                        {menuOpenId === headerWidget.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: '4px',
                              width: '150px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              padding: '4px',
                              zIndex: 999,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                onDuplicateWidget?.(headerWidget.id);
                                setMenuOpenId(null);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#334155',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                            >
                              <Copy size={12} color="#f97316" />
                              <span>Duplicate Header</span>
                            </button>

                            <div style={{ height: '1px', backgroundColor: '#f1f5f9', margin: '3px 0' }} />

                            <button
                              type="button"
                              onClick={() => {
                                onDeleteWidget?.(headerWidget.id);
                                setMenuOpenId(null);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#ef4444',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                            >
                              <Trash2 size={12} color="#ef4444" />
                              <span>Delete Header</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {renderWidgetContent(headerWidget || { id: 'master-header', type: 'header', properties: effectiveHeaderProps, x: 0, y: 0, w: 12, h: 1 }, index, pages.length)}
                </Box>
              )}

              {/* Main Draggable Body Grid */}
              <Box sx={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'visible' }}>
                <GridLayout
                  className="layout"
                  layout={layout}
                  width={gridWidth}
                  gridConfig={{
                    cols: 12,
                    rowHeight: 32,
                    margin: [8, 8],
                    containerPadding: [0, 0],
                    maxRows: maxGridRows
                  }}
                  compactor={verticalCompactor}
                  dragConfig={{ enabled: !readOnly, bounded: false }}
                  resizeConfig={{ enabled: !readOnly, handles: ['se', 's', 'e'] }}
                  onLayoutChange={(newLayout) => onLayoutChange(pageId, newLayout)}
                >
                  {bodyWidgets.map((widget) => {
                    const isSelected = !readOnly && selectedId === widget.id;
                    return (
                      <div
                        key={widget.id}
                        data-grid={{
                          i: widget.id,
                          x: Number.isFinite(widget.x) ? widget.x : 0,
                          y: Number.isFinite(widget.y) ? widget.y : 0,
                          w: widget.w || 4,
                          h: widget.h || 4
                        }}
                        style={{
                          boxSizing: 'border-box',
                          border: isSelected ? '2px solid #f97316' : 'none',
                          borderRadius: '0px',
                          padding: '0px',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          position: 'relative',
                          overflow: 'visible',
                          zIndex: isSelected ? 50 : 1,
                          cursor: readOnly ? 'default' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!readOnly && !isSelected) {
                            e.currentTarget.style.outline = '1px dashed rgba(249, 115, 22, 0.35)';
                            e.currentTarget.style.outlineOffset = '-1px';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!readOnly && !isSelected) {
                            e.currentTarget.style.outline = 'none';
                          }
                        }}
                        onClick={(e) => {
                          if (readOnly) return;
                          e.stopPropagation();
                          onSelectWidget(widget.id, pageId);
                        }}
                      >
                        {/* On-Canvas Floating Toolbar for Selected Widget */}
                        {!readOnly && isSelected && (
                          (widget.type === 'heading' || widget.type === 'text') ? (
                            <CanvasFloatingTextToolbar
                              widget={widget}
                              pageIndex={index}
                              totalPages={pages.length}
                              onUpdateWidget={onUpdateWidget}
                              onDuplicateWidget={onDuplicateWidget}
                              onDeleteWidget={onDeleteWidget}
                              onMoveWidgetToPage={onMoveWidgetToPage}
                            />
                          ) : (
                            (() => {
                              const isAtTop = (widget.y || 0) <= 1;
                              const isNearRightEdge = (widget.x || 0) + (widget.w || 4) >= 12;
                              const isNearLeftEdge = (widget.x || 0) === 0 && (widget.w || 4) <= 3;

                              return (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: isAtTop ? '6px' : '-32px',
                                    ...(isNearLeftEdge ? { left: '4px' } : { right: isNearRightEdge ? '4px' : '0px' }),
                                    zIndex: 100,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    backgroundColor: '#0f172a',
                                    padding: '3px 4px',
                                    borderRadius: '6px',
                                    border: '1px solid #334155',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                    pointerEvents: 'auto'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {onMoveWidgetToPage && index > 0 && (
                                    <button
                                      type="button"
                                      title="Move to Previous Page"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onMoveWidgetToPage(widget.id, 'prev');
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 4px',
                                        borderRadius: '3px',
                                        color: '#94a3b8',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
                                    >
                                      <ArrowUp size={13} />
                                    </button>
                                  )}
                                  {onMoveWidgetToPage && (
                                    <button
                                      type="button"
                                      title={index < pages.length - 1 ? "Move to Next Page" : "Move to New Page"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onMoveWidgetToPage(widget.id, 'next');
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 4px',
                                        borderRadius: '3px',
                                        color: '#94a3b8',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
                                    >
                                      <ArrowDown size={13} />
                                    </button>
                                  )}
                                  {onMoveWidgetToPage && (
                                    <div style={{ width: '1px', height: '12px', backgroundColor: '#334155', margin: '0 1px' }} />
                                  )}
                                  {onDuplicateWidget && (
                                    <button
                                      type="button"
                                      title="Duplicate"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDuplicateWidget(widget.id);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 4px',
                                        borderRadius: '3px',
                                        color: '#94a3b8',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <Copy size={13} />
                                    </button>
                                  )}
                                  {onDeleteWidget && (
                                    <button
                                      type="button"
                                      title="Delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteWidget(widget.id);
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '3px 4px',
                                        borderRadius: '3px',
                                        color: '#ef4444',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              );
                            })()
                          )
                        )}

                        {renderWidgetContent(widget, index, pages.length)}
                      </div>
                    );
                  })}
                </GridLayout>
              </Box>

              {/* Pinned Bottom Footer Slot */}
              {isFooterEnabled && (
                <Box
                  sx={{
                    width: '100%',
                    height: 38,
                    flexShrink: 0,
                    position: 'relative',
                    mt: 'auto',
                    boxSizing: 'border-box',
                    border: readOnly
                      ? 'none'
                      : ((footerWidget && selectedId === footerWidget.id) ? '2px solid #f97316' : '1px solid transparent'),
                    borderRadius: '6px',
                    p: '2px 4px',
                    cursor: readOnly ? 'default' : 'pointer',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: (!readOnly && footerWidget && selectedId === footerWidget.id)
                      ? '0 4px 14px rgba(249, 115, 22, 0.15)'
                      : 'none',
                    '&:hover': {
                      borderColor: (!readOnly && footerWidget && selectedId === footerWidget.id) ? '#f97316' : (!readOnly ? '#cbd5e1' : 'transparent')
                    }
                  }}
                  onClick={(e) => {
                    if (readOnly) return;
                    e.stopPropagation();
                    if (footerWidget) {
                      onSelectWidget(footerWidget.id, pageId);
                    } else {
                      onSelectWidget(null, pageId);
                    }
                  }}
                >
                  {/* Floating Action Menu for Selected Footer */}
                  {!readOnly && footerWidget && selectedId === footerWidget.id && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-28px',
                        right: '0px',
                        zIndex: 100,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        backgroundColor: '#ffffff',
                        padding: '3px 4px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                        pointerEvents: 'auto'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          title="Footer Actions"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(menuOpenId === footerWidget.id ? null : footerWidget.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 3px',
                            borderRadius: '3px',
                            color: menuOpenId === footerWidget.id ? '#ea580c' : '#475569',
                            backgroundColor: menuOpenId === footerWidget.id ? '#ffedd5' : 'transparent',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#0f172a';
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            if (menuOpenId !== footerWidget.id) {
                              e.currentTarget.style.color = '#475569';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <MoreHorizontal size={14} />
                        </button>

                        {menuOpenId === footerWidget.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: '4px',
                              width: '150px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              padding: '4px',
                              zIndex: 999,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                onDuplicateWidget?.(footerWidget.id);
                                setMenuOpenId(null);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#334155',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Copy size={12} color="#f97316" />
                              <span>Duplicate Footer</span>
                            </button>

                            <div style={{ height: '1px', backgroundColor: '#f1f5f9', margin: '3px 0' }} />

                            <button
                              type="button"
                              onClick={() => {
                                onDeleteWidget?.(footerWidget.id);
                                setMenuOpenId(null);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                color: '#ef4444',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Trash2 size={12} color="#ef4444" />
                              <span>Delete Footer</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {renderWidgetContent(footerWidget || { id: 'master-footer', type: 'footer', properties: effectiveFooterProps, x: 0, y: 24, w: 12, h: 1 }, index, pages.length)}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      {!readOnly && (
        <Button
          variant="outlined"
          startIcon={<Plus />}
          onClick={(e) => {
            e.stopPropagation();
            onAddPage();
          }}
          sx={{
            mt: 2,
            bgcolor: '#1e293b', // slate-800
            color: '#f8fafc', // slate-50
            borderColor: '#475569', // slate-600
            '&:hover': {
              bgcolor: '#334155', // slate-700
              borderColor: '#64748b' // slate-500
            }
          }}
        >
          Add Page
        </Button>
      )}
    </Box>
  );
};

export default ReportCanvas;
